import { zodResolver } from "@hookform/resolvers/zod";
import { Building2 } from "lucide-react";
import React, { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useUpdateCompanyDetailsMutation } from "../../../api/useUpdateCompanyDetailsMutation";
import ClientConfigCard from "../components/ClientConfigCard";
import { useEditMode } from "../hooks/useEditMode";

import { useClientSchema } from "@/components/forms/schemas/clientSchema";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useAuth } from "@/lib/hooks/useAuth";
import useToast from "@/lib/hooks/useToast";
import { ClientType } from "@/types/client/ClientType";

const SECTION_NAME = "company-details";

type CompanyDetailsCardProps = {
  client?: ClientType;
  clientId: string;
};

const CompanyDetailsCard: React.FC<CompanyDetailsCardProps> = ({
  client,
  clientId,
}) => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { showToast } = useToast();
  const { isEditMode, editMode, viewMode } = useEditMode(SECTION_NAME);
  const [originalValues, setOriginalValues] = useState<{
    name?: string;
    imagePath?: string;
  }>({});

  const { clientLogo } = useClientSchema();
  const updateMutation = useUpdateCompanyDetailsMutation(
    clientId,
    tenantId || ""
  );

  // Create schema that only validates fields in this form
  const companyDetailsSchema = clientLogo.extend({
    name: z.string().optional(), // name is read-only in this form, so optional for validation
  });

  const methods = useForm<z.infer<typeof companyDetailsSchema>>({
    resolver: zodResolver(companyDetailsSchema),
    defaultValues: {
      name: client?.name || "",
      imagePath: client?.imagePath || "",
    },
  });

  const { watch, getValues, setValue, setError, handleSubmit, formState } =
    methods;

  const companyName = watch("name") || client?.name || "";
  const logoUrl = watch("imagePath") || client?.imagePath || "";

  const handleEdit = useCallback(() => {
    // Check if we have unsaved changes before requesting edit mode
    const current = getValues();
    const hasChanges =
      isEditMode &&
      (current.name !== originalValues.name ||
        current.imagePath !== originalValues.imagePath);

    const canEdit = editMode(hasChanges, () => {
      // Discard changes callback
      setValue("name", originalValues.name || client?.name || "");
      setValue(
        "imagePath",
        originalValues.imagePath || client?.imagePath || ""
      );
    });

    if (canEdit) {
      // Store original values
      setOriginalValues({
        name: current.name,
        imagePath: current.imagePath,
      });
    }
  }, [editMode, isEditMode, getValues, setValue, originalValues, client]);

  const handleCancel = useCallback(() => {
    // Reset to original values
    setValue("name", originalValues.name || client?.name || "");
    setValue("imagePath", originalValues.imagePath || client?.imagePath || "");
    viewMode();
  }, [originalValues, client, setValue, viewMode]);

  const handleSave = useCallback(() => {
    const formData = getValues();
    const trimmedImagePath = formData.imagePath?.trim() || "";
    const sectionData = {
      // Send empty string explicitly to clear the field, not undefined
      imagePath: trimmedImagePath,
      tenantId: tenantId || undefined,
    };

    updateMutation.mutate(sectionData, {
      onSuccess: () => {
        // Update form value to reflect cleared state
        setValue("imagePath", trimmedImagePath);
        setOriginalValues({
          name: formData.name,
          imagePath: trimmedImagePath,
        });
        viewMode();
        showToast(
          t("admin.client.clientAction.success", { action: "updated" }),
          "success"
        );
      },
      onError: (error) => {
        handleProblemDetailsError({
          error,
          t,
          setError,
          formValues: getValues(),
          showToast,
        });
      },
    });
  }, [getValues, updateMutation, tenantId, t, showToast, setError, viewMode, setValue]);

  const onSubmitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit(
        () => {
          handleSave();
        },
        () => {
          // Validation errors are handled by react-hook-form
        }
      )(e);
    },
    [handleSubmit, handleSave]
  );

  const editContent = (
    <div className="gap-4 flex flex-col">
      <FormField
        control={methods.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientInfo.companyName")}
              <span className="text-destructive ml-1">*</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                variant="outline"
                value={field.value || ""}
                disabled={true}
                placeholder={t("admin.client.addClient.clientInfo.companyName")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={methods.control}
        name="imagePath"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientSettings.logoUrl")}
            </FormLabel>
            <FormControl>
              <Input
                variant="outline"
                {...field}
                value={field.value || ""}
                placeholder="https://www.domain.nl/logo.png"
                autoFocus={isEditMode}
              />
            </FormControl>
            {formState.errors.imagePath ? (
              <FormMessage />
            ) : (
              <FormDescription>
                {t("admin.client.addClient.clientSettings.logoNotice")}
              </FormDescription>
            )}
          </FormItem>
        )}
      />
    </div>
  );

  const viewContent = (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientInfo.companyName")}
        </label>
        <div className="text-base font-normal text-foreground">
          {companyName || "-"}
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientSettings.logoUrl")}
        </label>
        <div className="text-base font-normal text-foreground">
          {logoUrl || "-"}
        </div>
      </div>
    </div>
  );

  return (
    <FormProvider {...methods}>
      <ClientConfigCard
        title={t("admin.client.editClientTabs.companyDetails")}
        icon={Building2}
        isEditMode={isEditMode}
        isPending={updateMutation.isPending}
        onEdit={handleEdit}
        onCancel={handleCancel}
        editContent={editContent}
        onSubmit={onSubmitHandler}
      >
        {viewContent}
      </ClientConfigCard>
    </FormProvider>
  );
};

export default CompanyDetailsCard;
